import React, { useState } from "react";
import { CrudPage } from "@/components/CrudPage";
import { RATE_TYPE } from "@/lib/constants";
import { yen } from "@/lib/format";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const RATE_MAP = { fixed: "固定", variable: "変動" };

const columns = [
  { key: "lender", label: "金融機関", render: (r) => <span className="font-medium">{r.lender}</span> },
  { key: "property_id", label: "対象物件", render: (r, h) => h.nameOf("properties", r.property_id) },
  { key: "principal", label: "借入額", render: (r, h) => h.yen(r.principal) },
  { key: "current_balance", label: "現在残高", render: (r, h) => h.yen(r.current_balance) },
  { key: "interest_rate", label: "金利", render: (r) => `${r.interest_rate}% (${RATE_MAP[r.rate_type] || "-"})` },
  { key: "monthly_payment", label: "毎月返済額", render: (r, h) => h.yen(r.monthly_payment) },
];

const fields = [
  { name: "lender", label: "金融機関", required: true },
  { name: "property_id", label: "対象物件", type: "select", lookup: "properties" },
  { name: "borrow_date", label: "借入日", type: "date" },
  { name: "principal", label: "借入額", type: "currency" },
  { name: "current_balance", label: "現在残高", type: "currency" },
  { name: "interest_rate", label: "金利(%)", type: "number" },
  { name: "rate_type", label: "固定・変動", type: "select", options: RATE_TYPE, default: "fixed" },
  { name: "term_years", label: "返済期間(年)", type: "number" },
  { name: "monthly_payment", label: "毎月返済額", type: "currency" },
  { name: "final_date", label: "最終返済日", type: "date" },
  { name: "collateral", label: "担保情報", colSpan: 2 },
];

export default function Loans() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  const openSchedule = async (row) => {
    setLoading(true);
    setSchedule({ loan: row, rows: [] });
    try {
      const { data } = await api.get(`/loans/${row.id}/schedule`);
      setSchedule({ loan: row, rows: data.rows });
    } catch {} finally { setLoading(false); }
  };

  return (
    <>
      <CrudPage
        title="ローン管理"
        subtitle="不動産ごとの融資情報（行クリックで返済予定表）"
        endpoint="/loans"
        resource="loans"
        columns={columns}
        fields={fields}
        testid="loans"
        addLabel="ローンを登録"
        onView={openSchedule}
        searchKeys={["lender"]}
      />

      <Dialog open={!!schedule} onOpenChange={(o) => !o && setSchedule(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{schedule?.loan?.lender} 返済予定表（今後12ヶ月）</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          ) : (
            <div className="overflow-y-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2">回</th><th className="px-3 py-2">返済額</th>
                    <th className="px-3 py-2">元金</th><th className="px-3 py-2">利息</th><th className="px-3 py-2">残高</th>
                  </tr>
                </thead>
                <tbody>
                  {(schedule?.rows || []).map((r) => (
                    <tr key={r.month} className="border-b last:border-0">
                      <td className="px-3 py-2">{r.month}</td>
                      <td className="px-3 py-2">{yen(r.payment)}</td>
                      <td className="px-3 py-2">{yen(r.principal)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{yen(r.interest)}</td>
                      <td className="px-3 py-2 font-medium">{yen(r.balance)}</td>
                    </tr>
                  ))}
                  {(!schedule?.rows || schedule.rows.length === 0) && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">返済額・金利を登録すると予定表が表示されます</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
